import React, { useContext, useRef } from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Print } from "@mui/icons-material";
import { useAuth } from "oidc-react";
import { TransactionDetails } from "../../../models/transactions";
import { PrintContext } from "../context/PrintContext";
import { apiFetch } from "../../../api/fetch";
import getPrintableDetails from "../print/printTransactionDetails";
import { UserContext } from "../../../context/UserContext";
import { hasFeature, hasModule } from "../../../helpers/helpers";
import { REPORT_TYPE_DETAILS, REPORT_TYPE_UNSET } from "../../../api/transactions";

interface EnhancedTablePrinterToolbarProps {
    companyId: string;
    setPrintButtonPushed?: (pushed: boolean) => void;
    selectedRows?: string[];
    adminView?: boolean;
}

export const EnhancedTablePrinterToolbar = (props: EnhancedTablePrinterToolbarProps) => {
    const { companyId, setPrintButtonPushed, selectedRows = [], adminView } = props;
    const { t } = useTranslation();
    const { setIsPrinting, setReportType, setProgress } = useContext(PrintContext);
    const { locale, userModules, labsFeatures } = useContext(UserContext);
    const hasModuleInvoiceNumber = hasModule(userModules, "ModuleInvoiceNumbers");
    const hasModuleTicketNumber = hasModule(userModules, "ModuleTicketNumber");
    const hasFeatureCCSurcharge = hasFeature(labsFeatures, "CCSurcharge");
    const auth = useAuth();
    const maybeAccessToken = auth?.userData?.access_token;

    const hasTriggeredRef = useRef(false);

    const handleCancelClick = () => {
        setPrintButtonPushed?.(false);
    };

    const handlePrintClick = async () => {
        if (selectedRows.length === 0 || hasTriggeredRef.current) return;

        hasTriggeredRef.current = true;
        setTimeout(() => {
            hasTriggeredRef.current = false;
        }, 1500);

        setReportType(REPORT_TYPE_DETAILS);
        setIsPrinting(true);
        setProgress(0);

        const total = selectedRows.length;
        const completedRef = { current: 0 };

        try {
            const promises = selectedRows.map((id) => {
                const path = `/data/reporting/v1/${adminView ? `admin/` : ""}transactions/${companyId ? `${companyId}/` : ""}${id}`;

                return apiFetch<TransactionDetails>(path, {}, "GET", undefined, `Bearer ${maybeAccessToken}`)
                    .then((res) => {
                        completedRef.current += 1;
                        const pct = Math.round((completedRef.current / total) * 100);
                        setProgress(pct);
                        return res;
                    });
            });

            const results = await Promise.all(promises);

            const pdf = getPrintableDetails(
                {
                    companyId,
                    adminView,
                    translate: t,
                    locale,
                    hasFeatureCCSurcharge,
                    hasModuleInvoiceNumber,
                    hasModuleTicketNumber,
                },
                ...results
            );

            const filename = `transaction-details${selectedRows.length === 1 ? `-${selectedRows[0]}` : ''}.pdf`;

            const pdfBlob = pdf?.output?.("blob");
            if (pdfBlob) {
                const uri = URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
                const link = document.createElement('a');
                link.href = uri;
                link.download = filename || 'export.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(uri);
            } else {
                pdf.save(filename);
            }

        } catch (err) {
            console.error("Error generating/ downloading pdf", err);
        } finally {
            setProgress(100);
            setTimeout(() => {
                setIsPrinting(false);
                setReportType(REPORT_TYPE_UNSET);
                setProgress(undefined);
            }, 300);
        }
    };

    return (
        <Toolbar disableGutters sx={{ marginRight: "8px" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    marginLeft: "auto",
                    gap: "16px",
                    alignItems: "center",
                }}
            >
                <Button variant="outlined" onClick={handleCancelClick}>
                    {t("actions.cancel")}
                </Button>

                <Button
                    startIcon={<Print />}
                    variant="contained"
                    onClick={handlePrintClick}
                    disabled={selectedRows.length === 0}
                >
                    {`${t("screens.dashboarding.printSelected")} ${selectedRows.length > 0 ? t("export.selectedFormated", { selected: selectedRows.length }) : ""
                        }`}
                </Button>
            </Box>
        </Toolbar>
    );
};

export default EnhancedTablePrinterToolbar;
